import React from 'react';
import { Flex } from 'antd';
import { DayOfWeek } from '@/proto/logiblocks/logiblocks';
import { DisplayTimeSelection } from '@/pages/Shipments/MapExternalView/types';
import { DayOfWeekPicker } from '@/pages/Logiblocks/nodes/DayOfWeekPicker';
import SelectedEntriesHeader from './SelectedEntriesHeader';
import EntryChipList from './EntryChipList';
import { DayWindowEntry } from './dateSetTypes';
import { dayChipConfig, newDayEntry } from './dateSetUtils';

interface DayOfWeekEditorProps {
  entries: DayWindowEntry[];
  onChange: (entries: DayWindowEntry[]) => void;
  timezoneValue: DisplayTimeSelection;
  onTimezoneChange: (sel: DisplayTimeSelection) => void;
  disabled?: boolean;
}

/** The Day of the week tab body: day toggle buttons, "Selected days" header
 * with the timezone selector, and the day chips. Toggling a day adds or
 * removes its chip. */
const DayOfWeekEditor: React.FC<DayOfWeekEditorProps> = ({
  entries,
  onChange,
  timezoneValue,
  onTimezoneChange,
  disabled = false,
}) => {
  const selectedDays = entries.map((e) => e.day);

  const handleToggleChange = (days: DayOfWeek[]) => {
    const daySet = new Set(days);
    // Keep entries (and their time ranges) for days still selected, add an
    // all-day entry for each newly toggled-on day, drop the rest.
    const kept = entries.filter((e) => daySet.has(e.day));
    const keptDays = new Set(kept.map((e) => e.day));
    const added = days.filter((d) => !keptDays.has(d)).map(newDayEntry);
    onChange([...kept, ...added]);
  };

  return (
    <Flex vertical gap={12}>
      <DayOfWeekPicker
        selectedDays={selectedDays}
        onChange={handleToggleChange}
        disabled={disabled}
        allowEmpty
      />
      <SelectedEntriesHeader
        kind='daysOfWeek'
        count={entries.length}
        timezoneValue={timezoneValue}
        onTimezoneChange={onTimezoneChange}
      />
      <EntryChipList
        entries={entries}
        {...dayChipConfig}
        onChange={onChange}
        disabled={disabled}
      />
    </Flex>
  );
};

export default DayOfWeekEditor;
